import React, { useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import SEO from '../components/SEO';
import { FiMail, FiPhone, FiMapPin, FiClock, FiSend } from 'react-icons/fi';
import { useContent } from '../hooks/useContent';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CONTACT_FALLBACK = {
  hero_title_line1: "Let's Build",
  hero_title_highlight: 'Something Together',
  hero_description: 'For corporate inquiries, partnership opportunities, or media relations — our team is ready to connect.',
  address: 'D/2, Rani Plaza, Andheri-Kurla Road, Sakinaka, Mumbai – 400072',
  email: 'contact@arvaron.com',
  phone: '+91 98333 61155',
  business_hours: 'Mon-Fri: 9AM-6PM IST\nSat: 10AM-2PM IST'
};

const Contact = () => {
  const { content: cms } = useContent('contact', CONTACT_FALLBACK);
  const [formData, setFormData] = useState({
    name: '', email: '', company: '', phone: '', message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.message) {
      toast.error('Please fill in all required fields');
      return;
    }
    setIsSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/contact`, formData);
      toast.success('Message sent successfully! We\'ll get back to you soon.');
      setFormData({ name: '', email: '', company: '', phone: '', message: '' });
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputClass = "w-full px-5 py-4 bg-black/50 border border-white/10 rounded-lg text-white placeholder-white/30 focus:outline-none focus:border-[#17B9BE] focus:shadow-[0_0_20px_rgba(23,185,190,0.15)] transition-all text-sm";

  return (
    <div className="bg-black text-white" data-testid="contact-page">
      <SEO title="Contact" description={cms.hero_description} url="/contact" />

      {/* Hero Section */}
      <section className="relative h-[55vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab"
            alt="Corporate skyline"
            className="w-full h-full object-cover opacity-15"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/60 to-black" />
          <div className="absolute inset-0 hero-overlay" />
        </div>
        
        <div className="max-w-7xl mx-auto px-6 lg:px-8 z-10 text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <p className="text-xs uppercase tracking-[0.3em] text-[#17B9BE] mb-6">Inquiries</p>
            <h1 className="font-display text-5xl md:text-7xl font-bold mb-6" data-testid="contact-title">
              {cms.hero_title_line1}<br />
              <span className="brand-gradient-text">{cms.hero_title_highlight}</span>
            </h1>
            <p className="text-lg text-white/50 max-w-2xl mx-auto">
              {cms.hero_description}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Cards */}
      <section className="py-16 bg-black relative">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 -mt-24 relative z-20">
            {[
              { icon: FiMapPin, label: 'Headquarters', value: cms.address },
              { icon: FiMail, label: 'Email', value: cms.email, href: `mailto:${cms.email}` },
              { icon: FiPhone, label: 'Phone', value: cms.phone, href: `tel:${(cms.phone || '').replace(/\s/g, '')}` },
              { icon: FiClock, label: 'Business Hours', value: cms.business_hours },
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="bg-[#0a0a0a] p-6 rounded-lg border border-white/5 hover:border-[#17B9BE]/20 transition-all duration-300 group"
                data-testid={`contact-card-${i}`}
              >
                <div className="w-10 h-10 rounded-lg brand-gradient-bg flex items-center justify-center text-black mb-4 group-hover:shadow-[0_0_20px_rgba(23,185,190,0.3)] transition-shadow">
                  <item.icon size={18} />
                </div>
                <p className="text-xs uppercase tracking-wider text-[#17B9BE] mb-2 font-medium">{item.label}</p>
                {item.href ? (
                  <a href={item.href} className="text-sm text-white/70 hover:text-white transition-colors leading-relaxed">
                    {item.value}
                  </a>
                ) : (
                  <p className="text-sm text-white/70 leading-relaxed whitespace-pre-line">{item.value}</p>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-20 bg-black">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-16">
            {/* Left info panel */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="lg:col-span-2"
            >
              <p className="text-xs uppercase tracking-[0.3em] text-[#17B9BE] mb-4">Send a Message</p>
              <h2 className="font-display text-3xl font-bold mb-6">
                We'd love to <span className="brand-gradient-text">hear from you</span>
              </h2>
              <p className="text-white/40 mb-8 leading-relaxed">
                Whether you're exploring partnership opportunities, seeking our services, or have questions about our operations — fill out the form and our team will respond within 24 hours.
              </p>

              <div className="space-y-4">
                <div className="flex items-center space-x-3 text-white/40">
                  <div className="w-2 h-2 rounded-full bg-[#72EA43]" />
                  <span className="text-sm">Typically responds within 24 hours</span>
                </div>
                <div className="flex items-center space-x-3 text-white/40">
                  <div className="w-2 h-2 rounded-full bg-[#17B9BE]" />
                  <span className="text-sm">Partnership inquiries prioritized</span>
                </div>
                <div className="flex items-center space-x-3 text-white/40">
                  <div className="w-2 h-2 rounded-full bg-[#0999E1]" />
                  <span className="text-sm">Available across all time zones</span>
                </div>
              </div>
            </motion.div>

            {/* Form */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="lg:col-span-3"
            >
              <form onSubmit={handleSubmit} className="bg-[#0a0a0a] p-8 md:p-10 rounded-xl border border-white/5 relative overflow-hidden" data-testid="contact-form">
                {/* Subtle gradient glow top-right */}
                <div className="absolute -top-20 -right-20 w-40 h-40 bg-[#17B9BE]/5 rounded-full blur-3xl" />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
                  <div>
                    <label htmlFor="name" className="block text-xs uppercase tracking-wider text-white/40 mb-2 font-medium">Name *</label>
                    <input type="text" id="name" name="name" data-testid="contact-name-input" value={formData.name} onChange={handleChange} placeholder="Your full name" className={inputClass} required />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-xs uppercase tracking-wider text-white/40 mb-2 font-medium">Email *</label>
                    <input type="email" id="email" name="email" data-testid="contact-email-input" value={formData.email} onChange={handleChange} placeholder="you@company.com" className={inputClass} required />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
                  <div>
                    <label htmlFor="company" className="block text-xs uppercase tracking-wider text-white/40 mb-2 font-medium">Company</label>
                    <input type="text" id="company" name="company" data-testid="contact-company-input" value={formData.company} onChange={handleChange} placeholder="Your company" className={inputClass} />
                  </div>
                  <div>
                    <label htmlFor="phone" className="block text-xs uppercase tracking-wider text-white/40 mb-2 font-medium">Phone</label>
                    <input type="tel" id="phone" name="phone" data-testid="contact-phone-input" value={formData.phone} onChange={handleChange} placeholder="+91 ..." className={inputClass} />
                  </div>
                </div>

                <div className="mb-8">
                  <label htmlFor="message" className="block text-xs uppercase tracking-wider text-white/40 mb-2 font-medium">Message *</label>
                  <textarea id="message" name="message" data-testid="contact-message-input" value={formData.message} onChange={handleChange} rows="5" placeholder="Tell us about your inquiry..." className={inputClass} required></textarea>
                </div>

                <button
                  type="submit"
                  data-testid="contact-submit-button"
                  disabled={isSubmitting}
                  className="w-full group px-8 py-4 brand-gradient-bg text-black font-semibold rounded-lg hover:opacity-90 hover:shadow-[0_0_30px_rgba(23,185,190,0.25)] transition-all disabled:opacity-50 disabled:cursor-not-allowed text-sm uppercase tracking-wider flex items-center justify-center"
                >
                  {isSubmitting ? 'Sending...' : (
                    <>
                      Send Message
                      <FiSend className="ml-3 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" size={16} />
                    </>
                  )}
                </button>
              </form>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
